import { PaletteColors } from "$root/styles/themePalette";
import { type MessageSeverity, colorBySeverity } from "@mdotm/shadcn/legacy/components";
import { type Crumb, Breadcrumbs, MaybeSkeleton } from "@mdotm/shadcn/mdotm/components";
import { type NodeOrFn, renderNodeOrFn } from "@mdotm/shadcn/legacy/react-extensions";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { atomWithCompare, readAtom, WithAtom, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { type ReactNode, useCallback, useContext, useEffect } from "react";
import { PageCtx } from "./PageCtx";
import { instanceEqualsOrFastDeepEqual } from "@mdotm/shadcn/legacy/utils";

export type PageHeaderProps = {
	titleAction?: NodeOrFn<{ setTitleActionPortalTarget: PageCtx["setTitleActionPortalTarget"] }>;
	subTitle?: NodeOrFn;
	severity?: MessageSeverity | "calculating";
	crumbs: Crumb[];
	/**
	 * Full-width strip pinned to the bottom edge of the header. It lives inside the header's sticky
	 * container, so it stays visible on scroll without having to know the header's height — which
	 * varies with the subtitle. The strip owns its own background and horizontal padding.
	 */
	bottomBar?: ReactNode;
};

const header$ = atomWithCompare<PageHeaderProps | null>(null);
// const [title, setTitle] = useState()

export function PageHeader(props: PageHeaderProps): null {
	const propsRef = useUnsafeUpdatedRef(props);

	useEffect(() => () => writeAtom(header$, (current) => (propsRef.current === current ? null : current)), [propsRef]);

	const current = readAtom(header$);
	if (!instanceEqualsOrFastDeepEqual(current, props)) {
		writeAtom(header$, props);
	}
	return null;
}

export function PageHeaderOutlet(): React.JSX.Element {
	const CustomMappedColorBySeverity = { ...colorBySeverity, calculating: PaletteColors.YELLOW } satisfies Record<
		NonNullable<PageHeaderProps["severity"]>,
		string
	>;

	const ctx = useContext(PageCtx);
	const setTitleActionPortalTarget = useCallback(
		(el: HTMLDivElement | null) => {
			ctx?.setTitleActionPortalTarget(el);
		},
		[ctx],
	);

	return (
		<WithAtom atom$={header$}>
			{(headerState) => (
				<div
					className={`shadow-sm bg-white py-4 flex sticky z-40 flex-col min-w-0  ${!headerState?.severity ? "" : "border-b-2"}`}
					style={{
						margin: "-0.9375rem -0.9375rem 16px",
						top: 0,
						...(!headerState?.severity
							? {}
							: {
									borderColor: CustomMappedColorBySeverity[headerState.severity],
								}),
					}}
				>
					<div className="flex flex-wrap justify-between items-center px-6 gap-3">
						<div className="grow min-w-0">
							<MaybeSkeleton value={headerState} className="h-5 w-6">
								{(header) => (
									<Breadcrumbs
										syncHtmlTitle="Sphere"
										crumbs={header.crumbs.map((crumb, i) =>
											i !== header.crumbs.length - 1
												? crumb
												: {
														...crumb,
														"data-qualifier": "PageHeader/Title",
													},
										)}
									/>
								)}
							</MaybeSkeleton>
							<MaybeSkeleton value={headerState}>
								{(header) => (
									<div className="empty:hidden empty:mt-0 mt-2" data-qualifier="PageHeader/Subtitle">
										{typeof header.subTitle === "function" ? renderNodeOrFn(header.subTitle) : header.subTitle}
									</div>
								)}
							</MaybeSkeleton>
						</div>
						<MaybeSkeleton value={headerState} className="h-10 w-22 ml-auto">
							{(header) => (
								<div className="ml-auto">
									{typeof header.titleAction === "function" ? (
										renderNodeOrFn(header.titleAction, { setTitleActionPortalTarget })
									) : (
										<>
											{header.titleAction}
											<div className="empty:hidden" ref={setTitleActionPortalTarget} />
										</>
									)}
								</div>
							)}
						</MaybeSkeleton>
					</div>
					{/* -mb-4 pulls the strip through the container's own bottom padding, flush to the edge. */}
					{headerState?.bottomBar && <div className="mt-4 -mb-4">{headerState.bottomBar}</div>}
				</div>
			)}
		</WithAtom>
	);
}
